import { useState } from "react";
import { ArrowRightLeft, CalendarClock, ClipboardList, FilePlus2, Mail, MessageSquare, Phone, Plus, StickyNote } from "lucide-react";
import { noteCreate } from "@shared/schemas/notes";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { useFormValidation } from "@web/lib/formValidation";
import { fmtLocal, fmtStamp, fullName } from "@web/lib/format";
import { DISPOSITION_LABEL, isFailedStatus, leadStatusLabel, messageStatusLabel, tourStatusLabel, tourStatusTone } from "@web/lib/formatLeads";
import { leadApi, type LeadDetail, type LeadTimelineItem, type Tour } from "@web/lib/queries-leads";
import { Button, EmptyState, Inline, Row, RowGroup, Stack, StatusChip, Text, TextField, TimelineItem, UnsavedChanges, ValidationSummary } from "@web/ui";

type Props = { lead: LeadDetail; onTour: (t: Tour) => void };

type Msg = { channel: string; direction: string; subject: string | null; body: string; status: string; error: string | null; memberName: string | null; createdAt: string };
type Note = { body: string; memberName: string | null; pinned: number; createdAt: string };
type Status = { before: { status?: string } | null; after: { status?: string; lostReason?: string } | null; memberName: string | null; actorLabel: string | null; at: string };
type Activity = { action: string; memberName: string | null; actorLabel: string | null; at: string; after: Record<string, unknown> | null };

const ACTIVITY_TITLE: Record<string, string> = { create: "Lead created", dedupe_inquiry: "New inquiry matched this lead", convert: "Converted to booking" };

/** One timeline entry → icon / title / body / meta. */
function describe(item: LeadTimelineItem, contactName: string): { icon: React.ReactNode; title: string; body?: string; meta: string } {
  const d = item.data as unknown;
  if (item.type === "message") {
    const m = d as Msg;
    const who = m.direction === "in" ? contactName : (m.memberName ?? "System");
    const status = isFailedStatus(m.status) ? `${messageStatusLabel(m.status)}${m.error ? ` — ${m.error}` : ""}` : messageStatusLabel(m.status);
    if (m.channel === "call") return { icon: <Phone aria-hidden size={16} />, title: "Call logged", body: m.body, meta: `${who} · ${fmtStamp(m.createdAt)}` };
    if (m.channel === "email") return { icon: <Mail aria-hidden size={16} />, title: m.direction === "in" ? "Email received" : `Email · ${messageStatusLabel(m.status)} · ${m.subject ?? ""}`, body: m.body, meta: `${who} · ${fmtStamp(m.createdAt)} · ${status}` };
    return { icon: <MessageSquare aria-hidden size={16} />, title: m.direction === "in" ? "Text received" : `Text · ${messageStatusLabel(m.status)}`, body: m.body, meta: `${who} · ${fmtStamp(m.createdAt)} · ${status}` };
  }
  if (item.type === "note") {
    const n = d as Note;
    return { icon: <StickyNote aria-hidden size={16} />, title: n.pinned ? "Note (pinned)" : "Note", body: n.body, meta: `${n.memberName ?? "System"} · ${fmtStamp(n.createdAt)}` };
  }
  if (item.type === "tour") {
    const t = d as Tour;
    return { icon: <CalendarClock aria-hidden size={16} />, title: `Tour · ${tourStatusLabel(t.status)}`, body: `${fmtLocal(t.scheduledAt)} · ${t.durationMin} min${t.disposition ? ` · ${DISPOSITION_LABEL[t.disposition] ?? t.disposition}` : ""}${t.notes ? ` · ${t.notes}` : ""}`, meta: fmtStamp(t.createdAt) };
  }
  if (item.type === "status") {
    const s = d as Status;
    return { icon: <ArrowRightLeft aria-hidden size={16} />, title: `${leadStatusLabel(s.before?.status ?? "")} → ${leadStatusLabel(s.after?.status ?? "")}`, body: s.after?.lostReason, meta: `${s.memberName ?? s.actorLabel ?? "System"} · ${fmtStamp(s.at)}` };
  }
  const a = d as Activity;
  const body = a.action === "dedupe_inquiry" && a.after && typeof a.after.note === "string" ? a.after.note : undefined;
  return { icon: a.action === "create" ? <FilePlus2 aria-hidden size={16} /> : <ClipboardList aria-hidden size={16} />, title: ACTIVITY_TITLE[a.action] ?? a.action, body, meta: `${a.memberName ?? a.actorLabel ?? "System"} · ${fmtStamp(a.at)}` };
}

/** Timeline tab (SPEC §6.1 item 4): inline Add note, upcoming tours, then one stream of messages · calls · notes · tours · status changes. */
export function TimelineTab({ lead, onTour }: Props) {
  const [note, setNote] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const parsed = parseInput(noteCreate, { entityType: "lead", entityId: lead.id, body: note }, { type: "note" }, "note.save");
  const validation = useFormValidation(evaluation(parsed.issues, false));
  const { busy } = validation;
  const contactName = fullName(lead.contact) || "Client";
  const upcoming = lead.tours.filter((t) => t.status === "scheduled");

  async function addNote() {
    if (!parsed.success || !validation.begin("note.save")) return;
    setFailure(null);
    try {
      await leadApi.addNote(lead.id, parsed.data.body);
      setNote("");
      validation.clear();
    } catch (e) {
      validation.reject(e);
      setFailure(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <Stack gap={4}>
      <UnsavedChanges dirty={Boolean(note)} pending={busy} />
      <Stack as="form" gap={2} onSubmit={(e) => (e.preventDefault(), addNote())}>
        {(note.length > 0 || validation.unavailable) && <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />}
        {failure && <div role="alert"><Text tone="danger">{failure}</Text></div>}
        <Inline gap={2} align="end">
          <TextField label="Add note" placeholder="What happened?" className="flex-1" value={note} onChange={(e) => validation.edit(() => { setNote(e.target.value); setFailure(null); })} {...(note.length > 0 || validation.unavailable ? validation.field("body") : { "data-validation-field": "body", disabled: busy })} />
          <Button type="submit" fullWidth={false} variant="secondary" loading={busy} disabled={!note.trim()} icon={<Plus aria-hidden size={18} />}>
            Add
          </Button>
        </Inline>
      </Stack>
      {upcoming.length > 0 && (
        <Stack gap={2}>
          <Text variant="headline">Upcoming tour{upcoming.length > 1 ? "s" : ""}</Text>
          <RowGroup>
            {upcoming.map((t) => (
              <Row key={t.id} icon={<CalendarClock aria-hidden size={22} />} title={fmtLocal(t.scheduledAt)} subtitle={`${lead.venue?.name ?? ""} · ${t.durationMin} min · Complete, no-show, reschedule, cancel`} trailing={<StatusChip tone={tourStatusTone(t.status)}>{tourStatusLabel(t.status)}</StatusChip>} onClick={() => onTour(t)} />
            ))}
          </RowGroup>
        </Stack>
      )}
      {lead.timeline.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Nothing yet" body="Texts, calls, notes and tours will show up here." />
      ) : (
        <Stack gap={0}>
          {lead.timeline.map((it, i) => {
            const v = describe(it, contactName);
            return <TimelineItem key={`${it.type}-${it.id}`} icon={v.icon} title={v.title} body={v.body || undefined} meta={v.meta} last={i === lead.timeline.length - 1} />;
          })}
        </Stack>
      )}
    </Stack>
  );
}
